import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";
import { toast } from "react-toastify";
import BuyerDashboardModuleNav, { useBuyerDashboardNav } from "../../components/BuyerDashboardModuleNav";
import { filterBuyerDashboardOverviewData } from "../../config/buyerDashboardModules";
import { getBuyerDashboardModuleActions } from "../../config/buyerDashboardModuleUi";
import buyerDashboardService from "../../services/buyerDashboard.service";
import "../../assets/styles/buyer_dashboard.scss";

const DATE_PRESETS = [
  { key: "week", label: "This Week", days: 7 },
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "month", label: "This month", type: "this_month" },
  { key: "prev_month", label: "Last month", type: "prev_month" },
];

function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getDefaultDateRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 6);
  return { dateFrom: toIsoDate(from), dateTo: toIsoDate(to) };
}

function resolvePresetRange(preset) {
  const today = new Date();
  const to = toIsoDate(today);
  if (preset.type === "this_month") {
    return { dateFrom: toIsoDate(new Date(today.getFullYear(), today.getMonth(), 1)), dateTo: to };
  }
  if (preset.type === "prev_month") {
    return {
      dateFrom: toIsoDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      dateTo: toIsoDate(new Date(today.getFullYear(), today.getMonth(), 0)),
    };
  }
  const from = new Date();
  from.setDate(today.getDate() - (preset.days - 1));
  return { dateFrom: toIsoDate(from), dateTo: to };
}

function formatCurrency(value) {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `₹ ${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `₹ ${(n / 1_000).toFixed(0)}K`;
  return `₹ ${n.toLocaleString("en-IN")}`;
}

function formatMetricValue(metric) {
  if (metric.format === "currency") return formatCurrency(metric.value);
  if (metric.format === "percent") return `${metric.value}%`;
  return metric.value ?? 0;
}

function workQueueStatusClass(statusKey) {
  if (statusKey === "open") return "bd-wq-status--open";
  if (statusKey === "high") return "bd-wq-status--high";
  return "bd-wq-status--pending";
}

function DateRangePicker({ dateFrom, dateTo, onApply, onClose }) {
  const [draftFrom, setDraftFrom] = useState(dateFrom);
  const [draftTo, setDraftTo] = useState(dateTo);

  useEffect(() => {
    setDraftFrom(dateFrom);
    setDraftTo(dateTo);
  }, [dateFrom, dateTo]);

  return (
    <div className="bd-date-picker">
      <div className="bd-date-picker__presets">
        {DATE_PRESETS.map((preset) => (
          <button
            key={preset.key}
            type="button"
            className="bd-date-picker__preset"
            onClick={() => {
              onApply({ ...resolvePresetRange(preset), label: preset.label });
              onClose();
            }}
          >
            {preset.label}
          </button>
        ))}
      </div>
      <div className="bd-date-picker__custom">
        <label className="bd-date-picker__field">
          <span>From</span>
          <input type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
        </label>
        <label className="bd-date-picker__field">
          <span>To</span>
          <input type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
        </label>
      </div>
      <div className="bd-date-picker__actions">
        <button type="button" className="bd-date-picker__btn bd-date-picker__btn--ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="bd-date-picker__btn bd-date-picker__btn--primary"
          onClick={() => {
            if (draftFrom > draftTo) onApply({ dateFrom: draftTo, dateTo: draftFrom, label: "Custom range" });
            else onApply({ dateFrom: draftFrom, dateTo: draftTo, label: "Custom range" });
            onClose();
          }}
        >
          Apply
        </button>
      </div>
    </div>
  );
}

function SnapshotCard({ title, metrics }) {
  return (
    <div className="bd-panel">
      <div className="bd-panel__head">{title}</div>
      <div className="bd-panel__body">
        <div className="bd-snapshot-list">
          {(metrics || []).map((row) => (
            <div className="bd-snapshot-row" key={row.key}>
              <span className="bd-snapshot-row__label">{row.label}:</span>
              <span className="bd-snapshot-row__value">{row.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="buyer-dashboard">
      <div className="bd-skeleton" style={{ width: 260, height: 32, marginBottom: 8 }} />
      <div className="bd-skeleton" style={{ width: 480, height: 16, marginBottom: 16 }} />
      <div className="bd-skeleton" style={{ width: "100%", height: 40, marginBottom: 16 }} />
      <div className="row g-3 buyer-dashboard__alert-grid">
        {Array.from({ length: 6 }).map((_, i) => (
          <div className="col-6 col-md-4 col-xl-2" key={i}>
            <div className="bd-skeleton" style={{ height: 96, borderRadius: 10 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BuyerDashboard({ moduleKey = "overview" }) {
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const { navCtx } = useBuyerDashboardNav();
  const [dateFrom, setDateFrom] = useState(defaultRange.dateFrom);
  const [dateTo, setDateTo] = useState(defaultRange.dateTo);
  const [periodLabel, setPeriodLabel] = useState("This Week");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const apiModule = moduleKey === "overview" ? undefined : moduleKey;

  const load = useCallback(
    async (silent = false, range = { dateFrom, dateTo }) => {
      if (!silent) setLoading(true);
      try {
        const res = await buyerDashboardService.getDashboard({ ...range, module: apiModule });
        setData(res?.data ?? res);
      } catch (e) {
        if (!silent) {
          toast.error(e?.response?.data?.message || e?.message || "Could not load dashboard");
          setData(null);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [dateFrom, dateTo, apiModule]
  );

  useEffect(() => {
    void load(false, { dateFrom, dateTo });
  }, [dateFrom, dateTo, apiModule]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const interval = setInterval(() => void load(true, { dateFrom, dateTo }), 60000);
    return () => clearInterval(interval);
  }, [load, dateFrom, dateTo]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onDocClick = (e) => {
      if (!e.target.closest(".buyer-dashboard__date-wrap")) setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [pickerOpen]);

  if (loading) return <DashboardSkeleton />;

  if (!data) {
    return (
      <div className="buyer-dashboard">
        <p className="text-danger">Dashboard data unavailable.</p>
        <button type="button" className="btn btn-sm btn-primary" onClick={() => void load()}>
          Retry
        </button>
      </div>
    );
  }

  const dashboard =
    moduleKey === "overview" ? filterBuyerDashboardOverviewData(data, navCtx) : data;
  const showModuleActions = navCtx.canAccessBuyerDashboard;
  const isModuleDashboard = moduleKey !== "overview";
  const isModuleData = isModuleDashboard ? data?.module === moduleKey : true;
  const moduleActions = getBuyerDashboardModuleActions(moduleKey);
  const pipelineTitle =
    dashboard.pipeline_label ||
    (isModuleDashboard ? `${dashboard.module_label || "Module"} Pipeline` : "Overview Pipeline");
  const workQueueTitle =
    dashboard.work_queue_label ||
    (isModuleDashboard ? `${dashboard.module_label || "Module"} Work Queue` : "Overview Work Queue");
  const pipelineSteps = isModuleDashboard && !isModuleData ? [] : dashboard.overview_pipeline || [];
  const workQueueRows = isModuleDashboard && !isModuleData ? [] : dashboard.work_queue || [];

  return (
    <div className="buyer-dashboard">
      <div className="buyer-dashboard__intro">
        <h1 className="buyer-dashboard__title">Buyer Dashboard</h1>
        <p className="buyer-dashboard__subtitle">
          Role-based command centre with contextual module tabs and detailed dashboard sections.
        </p>
      </div>

      <BuyerDashboardModuleNav showSubTabs={isModuleDashboard} />

      <div className="buyer-dashboard__action-row">
        <div className="buyer-dashboard__date-wrap">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm bd-btn bd-btn--filter"
            onClick={() => setPickerOpen((v) => !v)}
            aria-expanded={pickerOpen}
          >
            <span>{periodLabel}</span>
            <FaChevronDown />
          </button>
          {pickerOpen && (
            <DateRangePicker
              dateFrom={dateFrom}
              dateTo={dateTo}
              onApply={({ dateFrom: f, dateTo: t, label }) => {
                setDateFrom(f);
                setDateTo(t);
                if (label) setPeriodLabel(label);
              }}
              onClose={() => setPickerOpen(false)}
            />
          )}
        </div>
        {showModuleActions && (
          <>
            {moduleActions.map((action) =>
              action.primary ? (
                <Link key={action.to} to={action.to} className="btn btn-sm bd-btn bd-btn--primary">
                  {action.label}
                </Link>
              ) : (
                <Link
                  key={action.to}
                  to={action.to}
                  className="btn btn-sm btn-outline-secondary bd-btn bd-btn--outline"
                >
                  {action.label}
                </Link>
              )
            )}
          </>
        )}
      </div>

      <div className="row g-3 buyer-dashboard__alert-grid">
        {(dashboard.status_alerts || []).map((alert) => (
          <div className="col-6 col-md-4 col-xl-2" key={alert.key}>
            <div className="bd-alert-card h-100">
              <div className="bd-alert-card__top">
                <span className={`bd-alert-card__count bd-alert-card__count--${alert.tone}`}>{alert.count}</span>
              </div>
              <div className="bd-alert-card__label">{alert.label}</div>
              <Link to={alert.href} className="bd-alert-card__view">
                View
              </Link>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 buyer-dashboard__metrics-grid">
        {(dashboard.key_metrics || []).map((metric) => (
          <div className="col-6 col-md-4 col-xl-2" key={metric.key}>
            <div className="bd-metric-card h-100">
              <div className="bd-metric-card__label">{metric.label}</div>
              <div className="bd-metric-card__value">{formatMetricValue(metric)}</div>
              {metric.sublabel && <div className="bd-metric-card__sub">{metric.sublabel}</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="bd-panel buyer-dashboard__pipeline-panel">
        <div className="bd-panel__head">{pipelineTitle}</div>
        <div className="bd-panel__body">
          <div className="row g-2 bd-overview-pipeline">
            {(pipelineSteps).map((step) => (
              <div className="col-6 col-md-3 col-xl" key={step.key}>
                <div className={`bd-overview-pipeline__step bd-overview-pipeline__step--${step.tone} h-100`}>
                  <div className="bd-overview-pipeline__count">{step.count}</div>
                  <div className="bd-overview-pipeline__label">{step.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {!isModuleDashboard && (
        <div className="row g-3 buyer-dashboard__module-grid">
          {(dashboard.module_summaries || []).map((mod) => (
            <div className="col-md-4" key={mod.key}>
              <div className="bd-module-card h-100">
                <div className="bd-module-card__title">{mod.title}</div>
                <div className="bd-module-card__row">
                  <span>Active items:</span>
                  <strong>{mod.active_items}</strong>
                </div>
                <div className="bd-module-card__row">
                  <span>Pending actions:</span>
                  <strong>{mod.pending_actions}</strong>
                </div>
                <div className="bd-module-card__row bd-module-card__row--risk">
                  <span>Risk / overdue:</span>
                  <strong>{mod.risk_overdue}</strong>
                </div>
                <Link to={mod.href} className="btn btn-sm btn-outline-secondary bd-btn bd-btn--outline bd-module-card__btn">
                  View Details
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bd-panel buyer-dashboard__work-queue-panel">
        <div className="bd-panel__head">{workQueueTitle}</div>
        <div className="bd-panel__body bd-panel__body--flush">
          <div className="table-responsive">
            <table className="bd-table mb-0">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Product / Asset</th>
                  <th>Party</th>
                  <th>Amount</th>
                  <th>Due</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {(workQueueRows).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="bd-empty">
                      No items in the work queue
                    </td>
                  </tr>
                ) : (
                  workQueueRows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.doc_no}</td>
                      <td>{row.product_name}</td>
                      <td>{row.party_name}</td>
                      <td>{row.amount > 0 ? formatCurrency(row.amount) : "—"}</td>
                      <td>{row.due_label}</td>
                      <td>
                        <span className={`bd-wq-status ${workQueueStatusClass(row.status_key)}`}>
                          {row.status_label}
                        </span>
                      </td>
                      <td>
                        <Link to={row.href} className="bd-table__action">
                          {row.action_label}
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="row g-3 buyer-dashboard__snapshot-row">
        <div className="col-md-4">
          <SnapshotCard title="Performance Snapshot" metrics={dashboard.performance_snapshot} />
        </div>
        <div className="col-md-4">
          <SnapshotCard title="Recent Activity" metrics={dashboard.recent_activity} />
        </div>
        <div className="col-md-4">
          <SnapshotCard title="Exceptions" metrics={dashboard.exceptions} />
        </div>
      </div>
    </div>
  );
}
